import { useDepartmentText } from 'model/DepartmentTerminology';
import { Alert, Group, Loader, Modal, Stack } from '@mantine/core';
import { IconAlertTriangle, IconArrowRight } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import P1Medium from 'components/typography/P1Medium';
import P1Regular from 'components/typography/P1Regular';
import P2Regular from 'components/typography/P2Regular';
import panic from 'errors/panic';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

interface ConfirmDeviceDepartmentChangeModalProps {
  opened: boolean;
  loading?: boolean;
  originalDepartmentId: string;
  newDepartmentId: string;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Modal for confirming a device department change.
 */
export default function ConfirmDeviceDepartmentChangeModal({
  opened,
  loading = false,
  originalDepartmentId,
  newDepartmentId,
  onClose,
  onConfirm,
}: ConfirmDeviceDepartmentChangeModalProps) {
  const departmentText = useDepartmentText();
  const { getAction } = useApi();
  const { deviceId } = useParams();
  const [oldDepartmentName, setOldDepartmentName] = useState<string>('');
  const [newDepartmentName, setNewDepartmentName] = useState<string>('');
  const [checkingMove, setCheckingMove] = useState(false);
  const [canMoveToDepartment, setCanMoveToDepartment] = useState(false);
  const [moveBlockedReason, setMoveBlockedReason] = useState<string>('');

  const isBusy = loading || checkingMove;

  const validationMessage = useMemo(() => {
    if (canMoveToDepartment) {
      return '';
    }

    const reasonText: Record<string, string> = {
      different_organization: departmentText(
        'Zariadenie nie je možné presunúť do položky úrovne inej organizácie.',
        'Zariadenie nie je možné presunúť do strediska inej organizácie.'
      ),
      unsupported_device_subtype: departmentText(
        'Nová položka úrovne nepodporuje typ zariadenia.',
        'Nové stredisko nepodporuje typ zariadenia.'
      ),
      has_uncompleted_revisions: 'Zariadenie má nedokončené revízne správy alebo formuláre a nie je možné ho presunúť.',
    };

    if (moveBlockedReason) {
      return (
        reasonText[moveBlockedReason] ??
        departmentText(
          'Zmena položky úrovne nie je pre toto zariadenie povolená.',
          'Zmena strediska nie je pre toto zariadenie povolená.'
        )
      );
    }

    return departmentText(
      'Zmena položky úrovne nie je pre toto zariadenie povolená.',
      'Zmena strediska nie je pre toto zariadenie povolená.'
    );
  }, [departmentText, canMoveToDepartment, moveBlockedReason]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    const departmentGetAction = getAction('DepartmentGet');
    const canMoveAction = getAction('DeviceCanMoveToDepartment');

    setCheckingMove(true);
    setCanMoveToDepartment(false);
    setMoveBlockedReason('');

    if (!deviceId) {
      setMoveBlockedReason('chýba ID zariadenia');
      setCheckingMove(false);
      return;
    }

    Promise.all([
      departmentGetAction({ parameters: { departmentId: originalDepartmentId } }),
      departmentGetAction({ parameters: { departmentId: newDepartmentId } }),
      canMoveAction({
        parameters: {
          deviceId,
          departmentId: newDepartmentId,
        },
      }),
    ])
      .then(([oldDepartment, newDepartment, canMoveResponse]) => {
        setOldDepartmentName(oldDepartment.departmentName);
        setNewDepartmentName(newDepartment.departmentName);
        setCanMoveToDepartment(canMoveResponse.canMove);
        setMoveBlockedReason(canMoveResponse.reason ?? '');
      })
      .catch((error) => {
        setCanMoveToDepartment(false);
        panic(error);
      })
      .finally(() => {
        setCheckingMove(false);
      });
  }, [opened, originalDepartmentId, newDepartmentId, getAction, deviceId]);

  return (
    <Modal
      centered
      opened={opened}
      onClose={onClose}
      title={departmentText('Potvrdenie zmeny položky úrovne', 'Potvrdenie zmeny strediska')}
      closeOnClickOutside={!isBusy}
      closeOnEscape={!isBusy}
      closeButtonProps={{ loading: isBusy }}
    >
      <Stack spacing={16} mb={32}>
        <P1Regular>
          {departmentText(
            'Meníte položku úrovne zariadenia. Naozaj chcete pokračovať a uložiť zmenu položky úrovne?',
            'Meníte stredisko zariadenia. Naozaj chcete pokračovať a uložiť zmenu strediska?'
          )}
        </P1Regular>

        {checkingMove ? (
          <Group spacing={8}>
            <Loader size="sm" />
            <P2Regular>
              {departmentText(
                'Overujem možnosť presunu zariadenia do novej položky úrovne...',
                'Overujem možnosť presunu zariadenia do nového strediska...'
              )}
            </P2Regular>
          </Group>
        ) : (
          <Group spacing={8} noWrap>
            <P1Medium>
              {oldDepartmentName ||
                departmentText(`Položka úrovne #${originalDepartmentId}`, `Stredisko #${originalDepartmentId}`)}
            </P1Medium>
            <IconArrowRight size={18} />
            <P1Medium>
              {newDepartmentName ||
                departmentText(`Položka úrovne #${newDepartmentId}`, `Stredisko #${newDepartmentId}`)}
            </P1Medium>
          </Group>
        )}

        {!checkingMove && !canMoveToDepartment && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {validationMessage}
          </Alert>
        )}
      </Stack>

      <Group position="right" spacing={16}>
        <Button variant="subtle-gray" onClick={onClose} px={8} disabled={isBusy}>
          Zrušiť
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            if (!canMoveToDepartment) {
              return;
            }

            onConfirm();
          }}
          loading={loading}
          disabled={checkingMove || !canMoveToDepartment}
          data-autofocus
        >
          Potvrdiť
        </Button>
      </Group>
    </Modal>
  );
}
